
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getOutcomeColor } from "./conversationUtils";

interface ConversationMappingCardProps {
  conversation: any;
  formatDate?: (date: string) => string;
  formatUnixDate?: (unixSecs: number) => string;
  truncateId?: (id: string) => string;
}

const ConversationMappingCard = ({ 
  conversation, 
  formatDate = (date) => new Date(date).toLocaleString(), 
  formatUnixDate = (unixSecs) => new Date(unixSecs * 1000).toLocaleString(),
  truncateId = (id) => id?.substring(0, 8) || ""
}: ConversationMappingCardProps) => {
  if (!conversation) return null;
  
  // Handle different data formats for creation time
  const getFormattedCreationTime = () => {
    if (conversation.creation_time) {
      return formatDate(conversation.creation_time);
    }
    if (conversation.start_time_unix_secs) {
      return formatUnixDate(conversation.start_time_unix_secs);
    }
    if (conversation.call_start_time) {
      return formatDate(conversation.call_start_time);
    }
    if (conversation.created_at) {
      return formatDate(conversation.created_at);
    }
    return "Unknown time";
  };

  // Get the appropriate badge color for the outcome
  const getBadgeVariant = (outcome: string): "default" | "success" | "destructive" | "secondary" | "outline" => {
    if (!outcome) return "default";
    
    const outcomeStr = outcome.toLowerCase();
    
    if (outcomeStr === 'reactivated' || outcomeStr === 'callback_requested') {
      return "success";
    }
    
    if (outcomeStr === 'not_interested' || outcomeStr === 'declined') {
      return "secondary";
    }
    
    if (outcomeStr === 'no_response' || outcomeStr === 'wrong_number') {
      return "destructive";
    }
    
    return "default";
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex justify-between items-center">
          <div>
            <span className="font-medium">
              {conversation.lead_name || conversation.lead_first_name || "Unknown Lead"}
            </span>
            {" "}
            <span className="text-muted-foreground text-sm">
              ({conversation.external_number || conversation.to_number || "No phone"})
            </span>
          </div>
          
          {conversation.outcome && (
            <Badge variant={getBadgeVariant(conversation.outcome)}>
              {conversation.outcome}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div className="text-muted-foreground">ID:</div>
            <div className="font-mono">{truncateId(conversation.id || conversation.conversation_id)}</div>
            
            <div className="text-muted-foreground">Time:</div>
            <div>{getFormattedCreationTime()}</div>
            
            {(conversation.call_duration_secs || conversation.duration_seconds) && (
              <>
                <div className="text-muted-foreground">Duration:</div>
                <div>{conversation.call_duration_secs || conversation.duration_seconds} seconds</div>
              </>
            )}
            
            {conversation.dealership_name && (
              <>
                <div className="text-muted-foreground">Dealership:</div>
                <div>{conversation.dealership_name}</div>
              </>
            )}
            
            {conversation.agent_name && (
              <>
                <div className="text-muted-foreground">Agent:</div>
                <div>{conversation.agent_name}</div>
              </>
            )}
            
            {conversation.status && (
              <>
                <div className="text-muted-foreground">Status:</div>
                <div>{conversation.status}</div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConversationMappingCard;
